// src/lib/managers/mail/MailManager.ts

import type { QueryClient } from "@tanstack/react-query";
import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { setQueryDataOverFetch } from "../../utils/query/setQueryDataOverFetch";
import { ApiManager } from "../api/ApiManager";
import { CO2Manager } from "../co2";
import { CO2Saved } from "../co2/types/co2";
import { ContactManager } from "../contact";
import { CustomView, CustomViewManager } from "../customView";
import { ViewFilter } from "../customView/types/customViews";
import { DataManager } from "../dataManager/DataManager";
import { isThrottleError } from "../dataManager/graph/GraphThrottleError";
import { MailFolder } from "../dataManager/graph/types/graph";
import { SentMailsCriteria } from "../dataManager/IDataApi";
import { IaManager } from "../ia";
import { LicenseManager } from "../license";
import { MailAnalysisFlag } from "../user/MailAnalysisFlag";
import { UserProfileLoader } from "../user/UserProfileLoader";
import { MailDeletionResult, uniqueMailvistaId } from "./domain/mailIdentity";
import { MailActions } from "./MailActions";
import { MailAnalysisState } from "./MailAnalysisState";
import { MailReader } from "./MailReader";
import { MailRecord } from "./MailRecord";
import { MailSync } from "./MailSync";
import { MailViews } from "./MailViews";
import { INITIAL_ANALYSIS_IN_PROGRESS_KEY } from "./queries";
import { BulkActionResult, BulkMailAction } from "./types/bulkMailAction";
import {
    AttachmentPreview,
    AttachmentPreviewRef,
    LinkedEventDetails,
    Mail,
    MailAIDetails,
    MailEntity,
    MailIds,
    MailSend,
    MailThread,
    MailViewContent,
    ReplyForward,
    SmartLabel,
    TodoItem,
} from "./types/mail";
import {
    BaseMail,
    ForwardMailRequestBody,
    GetMailsFromInternetMessageIdsResponseBody,
    isQualifiedMail,
    QualifiedMail,
} from "./types/mail.api";
import { NavBarContent } from "./types/ui";
import { computeAnalysisFloor } from "./utils/computeAnalysisFloor";

// ponytail: plafond fixe d'une salve de rattrapage, le reste part au passage suivant ; à rendre configurable si un Record réel le dépasse.
const PENDING_REANALYSIS_CAP = 200;

/**
 * @class MailManager
 * Gère les opérations liées aux emails, leur analyse et leur synchronisation
 * ═══════════════════════════════════════════════════════════════════════════
 */
export class MailManager {
    private iaManager: IaManager;

    private CO2Manager: CO2Manager;

    private contactManager: ContactManager;

    private processingPromise: Promise<any> = Promise.resolve();

    private _firstUserAnalysisLaunched = false;

    /** Vrai une fois `initial-analysis-completed` reçu dans la session. */
    private _isInitialAnalysisCompleted = false;

    /** Vrai une fois analysés les mails reçus depuis la dernière connexion (utilisateur existant). */
    private _isStartupNewMailsAnalysisCompleted = false;

    /** Levée du verrou de démarrage, une seule fois même si `_startUp` est rejoué. */
    private _startupNewMailsAnalysisRelease: Promise<void> | null = null;

    /** `_startUp` en cours : `withRetry` peut en relancer un pendant que le précédent tourne encore. */
    private _runningStartUpCount = 0;

    private mailRecord: MailRecord;

    private mailReader: MailReader;

    private mailActions: MailActions;

    private mailViews: MailViews;

    private mailSync: MailSync;

    private analysisState: MailAnalysisState;

    private eventBus: EventBus;

    private apiManager: ApiManager;

    private dataManager: DataManager;

    private licenseManager: LicenseManager;

    private profileLoader: UserProfileLoader;

    private mailAnalysisFlag: MailAnalysisFlag;

    private customViewManager: CustomViewManager;

    private hash: number = Math.round(Math.random() * 100);

    // Debounce params for event 'navigation-asking-for-update'
    private navigationNeedUpdateInterval: ReturnType<typeof setTimeout> | null =
        null;
    private readonly NAV_UPDATE_DEBOUNCE_DELAY = 2000; // 2 sec
    private lastNavigationUpdatedExecutionTime: number = 0;

    // [#1672 — fix ④ RaceRE-04] Retry borné de la réconciliation des compteurs sur throttle (429).
    private readonly RECONCILE_VERIFY_MAX_RETRIES = 2;
    private readonly RECONCILE_VERIFY_RETRY_DELAY_MS = 500;

    // #endregion Properties

    /**
     * @constructor
     * Initialise un nouveau MailManager avec les dépendances nécessaires
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {EventBus} eventBus - Bus d'événements pour la communication
     * @input {DataManager} DataManager - Gestionnaire Graph API
     * @input {IaManager} iaManager - Gestionnaire IA
     * @output {void}
     */
    constructor(
        eventBus: EventBus,
        mailRecord: MailRecord,
        analysisState: MailAnalysisState,
        mailSync: MailSync,
        mailReader: MailReader,
        mailActions: MailActions,
        mailViews: MailViews,
        apiManager: ApiManager,
        dataManager: DataManager,
        licenseManager: LicenseManager,
        profileLoader: UserProfileLoader,
        mailAnalysisFlag: MailAnalysisFlag,
        iaManager: IaManager,
        customViewManager: CustomViewManager,
        CO2Manager: CO2Manager,
        contactManager: ContactManager,
        queryClient: QueryClient,
    ) {
        logger.info([LOGS.MailManager], `MailManager(${this.hash})`);

        this.eventBus = eventBus;
        this.mailRecord = mailRecord;
        this.analysisState = analysisState;
        this.mailSync = mailSync;
        this.mailReader = mailReader;
        this.mailActions = mailActions;
        this.mailViews = mailViews;
        this.apiManager = apiManager;
        this.dataManager = dataManager;
        this.licenseManager = licenseManager;
        this.profileLoader = profileLoader;
        this.mailAnalysisFlag = mailAnalysisFlag;
        this.iaManager = iaManager;
        this.customViewManager = customViewManager;
        this.CO2Manager = CO2Manager;
        this.contactManager = contactManager;

        setQueryDataOverFetch(
            queryClient,
            INITIAL_ANALYSIS_IN_PROGRESS_KEY,
            true,
        );
        const publishInitialAnalysisInProgress = () =>
            setQueryDataOverFetch(
                queryClient,
                INITIAL_ANALYSIS_IN_PROGRESS_KEY,
                this.isInitialAnalysisInProgress(),
            );
        this.eventBus.on("initial-analysis-completed", () => {
            this._isInitialAnalysisCompleted = true;
            publishInitialAnalysisInProgress();
        });
        this.eventBus.on(
            "startup-new-mails-analysis-completed",
            publishInitialAnalysisInProgress,
        );
    }

    /**
     * Analyse de démarrage en cours. Nouvel utilisateur : l'analyse de l'onboarding, terminée par
     * `initial-analysis-completed` (fin normale, échec, absence de licence). Utilisateur existant : l'analyse des
     * mails reçus depuis la dernière connexion, terminée par `startup-new-mails-analysis-completed` (y compris
     * sans nouveau mail ou sur échec du démarrage).
     */
    public isInitialAnalysisInProgress(): boolean {
        if (this.profileLoader._isUserJustCreated()) {
            return !this._isInitialAnalysisCompleted;
        }

        return !this._isStartupNewMailsAnalysisCompleted;
    }

    public getGeneralMails(
        mailFolder: MailFolder,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        return this.mailViews.getGeneralMails(mailFolder, from, top);
    }

    public getSmartLabelMails(
        label: SmartLabel,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        return this.mailViews.getSmartLabelMails(label, from, top);
    }

    public getCustomViewMails(
        customViewId: string,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        return this.mailViews.getCustomViewMails(customViewId, from, top);
    }

    public computeNavBarContent(): Promise<NavBarContent> {
        return this.mailViews.computeNavBarContent();
    }

    public getDashboardTips(): string {
        return this.mailViews.getDashboardTips();
    }

    public setMailFocus(
        uniqueMailvistaId: string,
        isFocus: boolean,
    ): Promise<boolean> {
        return this.mailActions.setMailFocus(uniqueMailvistaId, isFocus);
    }

    public updateMailSmartLabel(
        uniqueMailvistaId: string,
        smartLabel: SmartLabel,
    ): Promise<boolean> {
        return this.mailActions.updateMailSmartLabel(
            uniqueMailvistaId,
            smartLabel,
        );
    }

    public setMailProcessed(
        uniqueMailvistaId: string,
        isUserProcessed: boolean,
    ): Promise<boolean> {
        return this.mailActions.setMailProcessed(
            uniqueMailvistaId,
            isUserProcessed,
        );
    }

    public setReadStatus(
        uniqueMailvistaId: string,
        isRead: boolean,
    ): Promise<boolean> {
        return this.mailActions.setReadStatus(uniqueMailvistaId, isRead);
    }

    public sendMail(mailData: MailSend): Promise<boolean> {
        return this.mailActions.sendMail(mailData);
    }

    public replyToMail(
        internetMessageId: string,
        replyData: ReplyForward,
        replyAll?: boolean,
    ): Promise<boolean> {
        return this.mailActions.replyToMail(
            internetMessageId,
            replyData,
            replyAll,
        );
    }

    public forwardMail(payload: ForwardMailRequestBody): Promise<boolean> {
        return this.mailActions.forwardMail(payload);
    }

    /**
     * Supprime la copie exacte désignée, pas la copie canonique : la copie Sent d'un self-sent
     * peut partir sans toucher la copie Inbox.
     */
    public deleteMail(uniqueMailvistaId: string): Promise<boolean> {
        return this.mailActions.deleteMail(uniqueMailvistaId);
    }

    /**
     * `success` et `failed` sont des uniqueMailvistaId : la copie ciblée pour DELETE et READ,
     * la copie inbox pour FOCUS et PROCESSED.
     */
    public executeBulkAction(
        action: BulkMailAction,
        uniqueMailvistaIds: string[],
    ): Promise<BulkActionResult> {
        return this.mailActions.executeBulkAction(action, uniqueMailvistaIds);
    }

    public addTodoToDashboard(
        internetMessageId: string,
        todoId: string,
    ): Promise<boolean> {
        return this.mailActions.addTodoToDashboard(internetMessageId, todoId);
    }

    public removeTodoFromDashboard(
        internetMessageId: string,
        todoId: string,
    ): Promise<boolean> {
        return this.mailActions.removeTodoFromDashboard(
            internetMessageId,
            todoId,
        );
    }

    public setTodoState(
        internetMessageId: string,
        todoId: string,
        isDone: boolean,
    ): Promise<boolean> {
        return this.mailActions.setTodoState(internetMessageId, todoId, isDone);
    }

    public updateMailsWithLinkedEventDetails(
        linkedEventDetailsList: LinkedEventDetails[],
    ): Promise<void> {
        return this.mailActions.updateMailsWithLinkedEventDetails(
            linkedEventDetailsList,
        );
    }

    public applyDeletionResult(delta: MailDeletionResult): void {
        return this.mailActions.applyDeletionResult(delta);
    }

    public getMailThread(uniqueMailvistaId: string): Promise<MailThread> {
        return this.mailReader.getMailThread(uniqueMailvistaId);
    }

    public getMailFullBodyContentWithAttachments(
        internetMessageId: string,
    ): Promise<void> {
        return this.mailReader.getMailFullBodyContentWithAttachments(
            internetMessageId,
        );
    }

    public getAttachmentPreview(
        ref: AttachmentPreviewRef,
    ): Promise<string | null> {
        return this.mailReader.getAttachmentPreview(ref);
    }

    /**
     * Les pièces jointes non résolues sont omises : le résultat n'est pas aligné sur `refs`,
     * apparier par `uniqueMailvistaId` + `attachmentId`.
     */
    public getAttachmentsPreviews(
        refs: AttachmentPreviewRef[],
    ): Promise<AttachmentPreview[]> {
        return this.mailReader.getAttachmentsPreviews(refs);
    }

    /**
     * RAG d'abord, repli sur la recherche legacy. `ContactManager.resolveContacts` ne doit jamais
     * retomber sur cette méthode, sous peine de récursion.
     */
    public searchContact(searchString: string): Promise<string[]> {
        return this.mailReader.searchContact(searchString);
    }

    /**
     * Utilisé seulement par ConversationManager.
     */
    public getViewFilterMails(
        viewFilter: ViewFilter,
        limit?: number,
    ): Promise<Mail[]> {
        return this.mailReader.getViewFilterMails(viewFilter, limit);
    }

    /**
     * Utilisé seulement par ConversationManager (garde de la corbeille : une suppression demandée à
     * l'assistant n'est proposée que si le dossier corbeille est connu).
     */
    public getDeletedItemsFolderId(): string | undefined {
        return this.mailSync._getDeletedItemsFolderId();
    }

    /**
     * Utilisé seulement par ConversationManager (boîte « envoyes » de l'étape `lireMails`).
     */
    public getSentMails(
        criteria: SentMailsCriteria,
        limit: number,
    ): Promise<Mail[]> {
        return this.mailReader.getSentMails(criteria, limit);
    }

    public getViewFilterInternetMessageIds(
        viewFilter: ViewFilter,
        limit?: number,
    ): Promise<string[]> {
        return this.mailReader.getViewFilterInternetMessageIds(
            viewFilter,
            limit,
        );
    }

    /**
     * Copie exacte (`internetMessageId::parentFolderId`), ou null, contrairement à
     * `_getMailFromRecordById` qui renvoie la copie canonique.
     */
    public getMailFromRecordByUniqueMailvistaId(uid: string): Mail | null {
        return this.mailReader.getMailFromRecordByUniqueMailvistaId(uid);
    }

    public fetchMissingMailViewContentIfAny(
        internetMessageIds: string[],
    ): Promise<void> {
        return this.mailSync.fetchMissingMailViewContentIfAny(
            internetMessageIds,
        );
    }

    /**
     * Ne fetche pas : appeler d'abord `fetchMissingMailViewContentIfAny`. Les ids absents du Record
     * sont omis.
     */
    public getMailViewContentFromRecord(
        internetMessageIds: string[],
    ): readonly MailViewContent[] {
        return this.mailSync.getMailViewContentFromRecord(internetMessageIds);
    }

    // #region Initialisation and cleanup

    /**
     * Fetch monthly analysed email count and Limit -
     * IF NEW USER, await firstUserEmailsAnalysis() -
     * ELSE, await fetchFocusStartupMails() - (not awaited) fetchOtherStartupMails
     *
     */
    public async _initialize(): Promise<void> {
        logger.info(
            [LOGS.MailManager, LOGS.ServiceProvider],
            `MailManager.initialize()`,
        );

        // Fetch monthly analysed email count and Limit
        try {
            const userProfile = this.profileLoader.cachedProfile;
            if (userProfile) {
                this.analysisState.currentMonthEmailCount =
                    userProfile.monthlyAnalyzedMailCount;
            }
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `Error during Monthly Analyzed Mail Count fetching: ${error}`,
            );
            throw error;
        }

        try {
            const monthlyAnalysedMailsLimit =
                await this.licenseManager.getActiveMonthlyAnalysedMailsLimit();

            if (monthlyAnalysedMailsLimit) {
                this.analysisState.monthlyAnalysedMailsLimit =
                    monthlyAnalysedMailsLimit;
            }
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `Error during Monthly Analyzed Mail Limit fetching: ${error}`,
            );
            throw error;
        }

        if (
            this.analysisState.monthlyAnalysedMailsLimit &&
            this.analysisState.currentMonthEmailCount
        ) {
            const remainingQuota =
                this.analysisState.monthlyAnalysedMailsLimit -
                this.analysisState.currentMonthEmailCount;
            if (remainingQuota <= 0) {
                this.eventBus.emit("max-analysed-mails-reached");
            }
        }

        // Initial fetch of mail folder ids
        await this.mailViews._fetchFolderIds();

        // If user has just been created, we fetch firstUserEmailsAnalysis
        const isUserJustCreated = this.profileLoader._isUserJustCreated();

        if (isUserJustCreated) {
            if (this.licenseManager.hasValidLicense()) {
                logger.info(
                    LOGS.MailManager,
                    "Launching User first mails analysis ...",
                );
                try {
                    await this.firstUserEmailsAnalysis();
                } catch (error) {
                    logger.error(
                        LOGS.MailManager,
                        `firstUserEmailsAnalysis failed !!! : ${error}`,
                    );
                    logger.warn(
                        LOGS.MailManager,
                        `firstUserEmailsAnalysis will retry once after 2s... : ${error}`,
                    );
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    await this.firstUserEmailsAnalysis();
                }
            } else {
                logger.warn(
                    [LOGS.MailManager, LOGS.AnalysisLifecycle],
                    "firstEmailAnalysis skipped because newly created user has no license — emitting initial-analysis-completed immediately",
                );
                // Débloque ServicesProvider._runPhase3 immédiatement (pas besoin d'attendre le timeout 30s)
                this.eventBus.emit("initial-analysis-completed");
            }
        }
        // User already exists, we fetch StartupMails
        else {
            // Fetch mailIds to fetch at startup
            const {
                focusMailsInternetMessageIds,
                favoriteCustomViewsMailsInternetMessageIds,
                otherViewsMailsInternetMessageIds,
            } = this.customViewManager._getStartupMails();

            // (Not awaited) Launch initial mail fetch for Focus view, thent for others
            logger.info(
                LOGS.MailManager,
                "Launching fetchFocusStartupMails ...",
            );
            this.fetchFocusStartupMails(focusMailsInternetMessageIds).then(
                (result) => {
                    // (Not awaited)Launch initial mail fetch for all views
                    logger.info(
                        LOGS.MailManager,
                        "Launching (not awaited ) fetchOtherStartupMails ...",
                    );
                    this.fetchOtherStartupMails(
                        favoriteCustomViewsMailsInternetMessageIds,
                        otherViewsMailsInternetMessageIds,
                    );
                },
            );
        }

        // Le délai UI avant affichage du Dashboard est désormais géré par ServicesProvider (_runPhase3).
        logger.info(LOGS.MailManager, "_initialize() done with success");
    }

    /**
     * Fetch pending Analysis mails + Mails since last processed mail + fetchMissingMailsIfAnyByInternetMessageIds
     */
    public async _startUp(): Promise<void> {
        logger.info(
            [LOGS.MailManager, LOGS.ServiceProvider],
            "MailManager._startUp()",
        );

        this._runningStartUpCount += 1;

        // Fetch new mails since last processed mail + fetchMissingMailsIfAnyByInternetMessageIds
        try {
            // 1. Récupération de la dernière date de traitement des mails envoyés
            const lastProcessedMailSendDate: string =
                await this.apiManager.getLastProcessedMailSentDate(); // [WS-C / AB#1576] migré Core → Api-Backend
            logger.info(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `Last processed mail send date: ${lastProcessedMailSendDate}`,
            );

            // 2. Si on a une date de dernière analyse, récupérer les nouveaux mails depuis cette date
            //    Skippé pour les nouveaux utilisateurs : firstUserEmailsAnalysis vient de tout traiter
            // Nouvel utilisateur : firstUserEmailsAnalysis vient de tout traiter → on skip le rattrapage GetMailIdsSince.
            if (
                lastProcessedMailSendDate &&
                !this.profileLoader._isUserJustCreated()
            ) {
                logger.info(
                    [LOGS.MailManager, LOGS.AnalysisLifecycle],
                    "Fetching new mails since last processed date...",
                );

                const lastProcessedDate = new Date(
                    lastProcessedMailSendDate,
                ).toISOString();

                // Récupération des internetMessageIds des mails envoyés depuis la dernière date
                let newMailIds: MailIds[] = [];
                try {
                    newMailIds =
                        await this.apiManager.GetMailIdsSince(
                            lastProcessedDate,
                        );
                } catch (error) {
                    logger.error(
                        [LOGS.MailManager],
                        `Error in _startup during getMailInternetMessageIdsSinceDateSent : ${error}`,
                    );
                    logger.warn(
                        LOGS.MailManager,
                        "Retrying once after 2s delay ...",
                    );

                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    newMailIds =
                        await this.apiManager.GetMailIdsSince(
                            lastProcessedDate,
                        );
                }

                const newInternetMessageIds = newMailIds.map(
                    (m) => m.internetMessageId,
                );
                logger.info(
                    [LOGS.MailManager, LOGS.AnalysisLifecycle],
                    `Found ${newInternetMessageIds.length} new mails since ${lastProcessedMailSendDate}`,
                );

                // 3. Lancement de la récupération et analyse des mails
                if (newInternetMessageIds.length > 0) {
                    logger.info(
                        [LOGS.MailManager, LOGS.AnalysisLifecycle],
                        "Starting fetchMissingMailsIfAnyByInternetMessageIds...",
                    );
                    await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
                        newInternetMessageIds,
                        `MailManager._startUp`,
                        true,
                    );
                    logger.info(
                        [LOGS.MailManager, LOGS.AnalysisLifecycle],
                        "MailManager initialization completed",
                    );
                } else {
                    logger.info(
                        [LOGS.MailManager, LOGS.AnalysisLifecycle],
                        "No mails to process during initialization",
                    );
                }
            }

            // 4. Lancement du polling mails
            this.mailSync._startMailPolling();

            // 5. [Fix C — AB#1645] Réconciliation LÉGÈRE des compteurs SmartLabel (fire-and-forget, NON awaité).
            //   Point « idle » de fin de Phase 4. Vérifie l'existence provider-side de TOUT le store via
            //   l'endpoint verdicts-only `verifyMailsExistence` (AUCUN téléchargement de contenu, ∝ taille
            //   store, pas ∝ inbox) → retire du store les ids confirmés absents/inbox-disparue. Corrige les
            //   compteurs de TOUS les labels au boot sans attendre la 1re navigation, sans casser le
            //   lazy-loading. Contraintes produit A (1 passage, retrait des seuls confirmés) + B (non bloquant).
            void this._reconcileSmartLabelStoreCounters();
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `Error during MailManager _startup: ${error}`,
            );
            throw error;
        } finally {
            this._runningStartUpCount -= 1;

            // Toutes les analyses en cours, au dernier `_startUp` terminé : les nouveaux mails ont pu être lancés à
            // l'analyse avant (mails récents de l'inbox chargés au démarrage)
            if (this._runningStartUpCount === 0) {
                this._startupNewMailsAnalysisRelease ??= Promise.allSettled(
                    this.mailSync.getRunningAnalysisPasses(),
                ).then(() => {
                    this._isStartupNewMailsAnalysisCompleted = true;
                    this.eventBus.emit("startup-new-mails-analysis-completed");
                });
            }
        }
    }

    /**
     * @method _reconcileSmartLabelStoreCounters
     * (Fix C — AB#1645) Réconcilie les compteurs SmartLabel en fin de Phase 4, en mode LÉGER (verdicts-only).
     * ═══════════════════════════════════════════════════════════════════════════
     * Le store SmartLabel (CustomViewManager) est chargé en Phase 2 depuis la DB backend, 100 % pull-driven :
     * elle ignore une suppression externe (Outlook, app fermée) → compteurs gonflés jusqu'à la 1re navigation.
     *
     * On vérifie l'existence provider-side de TOUS les ids du store via `apiManager.verifyMailsExistence`
     * (endpoint backend verdicts-only : `$select` Graph minimal, AUCUN body/PJ, batché) puis on retire du
     * store les ids confirmés absents/inbox-disparue. Contrairement au fetch contenu, on ne télécharge AUCUN
     * mail → coût ∝ taille du store (borné), lazy-loading préservé même sur des comptes à centaines de mails.
     *
     * Best-effort : ne propage jamais (fire-and-forget via `void`). inboxFolderId requis (déc. #7).
     * @input {void}
     * @output {Promise<void>}
     * @private
     */
    private async _reconcileSmartLabelStoreCounters(): Promise<void> {
        try {
            if (!this.mailRecord.inboxFolderId) {
                logger.info(
                    [LOGS.MailManager],
                    `_reconcileSmartLabelStoreCounters — inboxFolderId absent, réconciliation ignorée`,
                );
                return;
            }

            const storeIds =
                this.customViewManager._getAllSmartLabelInternetMessageIds();

            // [#1672 — fix ② Q-3] Même sans SmartLabel à vérifier, on réconcilie la famille B (dossiers) :
            // une suppression externe de mail non-lu peut avoir changé un `unreadItemCount` de dossier
            // indépendamment du store SmartLabel. Le refetch Graph induit est INTENTIONNEL (Q-3 « tout doit
            // être réconcilié ») et borné par le debounce couche 1 — pas un no-op, mais un aller-retour
            // volontaire aux déclencheurs de réconciliation (essentiellement fin de Phase 4, boot).
            if (storeIds.length === 0) {
                this.mailViews._reconcileFolderCounters();
                return;
            }

            // [#1672 — fix ④ RaceRE-03] Snapshot des entrées AVANT l'appel async : on n'appliquera le verdict
            // de retrait que si l'entrée n'a pas changé entre-temps (mail ré-affirmé pendant le trou async).
            const snapshot =
                this.customViewManager._snapshotSmartLabelEntriesById(storeIds);

            logger.info(
                [LOGS.MailManager],
                `_reconcileSmartLabelStoreCounters — vérification de ${storeIds.length} id(s) du store SmartLabel`,
            );

            // [#1672 — fix ④ RaceRE-04] Retry borné sur throttle (429) : un 429 transitoire ne doit pas
            // abandonner la réconciliation (compteurs laissés stale).
            const { internetMessageIdsToRemove } =
                await this._verifyMailsExistenceWithRetry(
                    storeIds,
                    this.mailRecord.inboxFolderId,
                );

            let removed = 0;
            internetMessageIdsToRemove.forEach((internetMessageId) => {
                if (
                    this.customViewManager._removeMessageFromSmartLabelStoreIfUnchanged(
                        internetMessageId,
                        snapshot.get(internetMessageId),
                    )
                ) {
                    removed++;
                }
            });

            logger.info(
                [LOGS.MailManager],
                `_reconcileSmartLabelStoreCounters — ${removed}/${storeIds.length} mail(s) retiré(s) du store (supprimés/inbox-disparue)`,
            );

            // [#1672 — fix ② Q-3] Réconcilier AUSSI les compteurs DOSSIERS (famille B) : non-lus Graph.
            this.mailViews._reconcileFolderCounters();
        } catch (error) {
            // Best-effort : un échec ne doit jamais casser le démarrage (déjà non bloquant).
            logger.error(
                [LOGS.MailManager],
                `Fix C — réconciliation compteurs SmartLabel échouée : ${error}`,
            );
        }
    }

    /**
     * (#1672 — fix ④ RaceRE-04) Appelle `verifyMailsExistence` avec un retry borné sur throttle (429).
     * Détection robuste : erreur typée (`isThrottleError`) OU message contenant « 429 » / « too many
     * requests » (les 429 peuvent surfacer sous plusieurs formes). Backoff simple (délai constant) ;
     * best-effort — au-delà du plafond de tentatives, l'erreur est propagée au catch appelant.
     */
    private async _verifyMailsExistenceWithRetry(
        internetMessageIds: string[],
        inboxFolderId: string,
    ): Promise<{ internetMessageIdsToRemove: string[] }> {
        let lastError: unknown;
        for (
            let attempt = 0;
            attempt <= this.RECONCILE_VERIFY_MAX_RETRIES;
            attempt++
        ) {
            try {
                return await this.apiManager.verifyMailsExistence(
                    internetMessageIds,
                    inboxFolderId,
                );
            } catch (error) {
                lastError = error;
                const message = String(
                    (error as { message?: unknown })?.message ?? error,
                );
                const isThrottle =
                    isThrottleError(error) ||
                    /\b429\b|too many requests/i.test(message);
                if (
                    !isThrottle ||
                    attempt === this.RECONCILE_VERIFY_MAX_RETRIES
                ) {
                    throw error;
                }
                logger.warn(
                    [LOGS.MailManager],
                    `_reconcileSmartLabelStoreCounters — verify throttlé (429), retry ${attempt + 1}/${this.RECONCILE_VERIFY_MAX_RETRIES}`,
                );
                await new Promise<void>((resolve) =>
                    setTimeout(resolve, this.RECONCILE_VERIFY_RETRY_DELAY_MS),
                );
            }
        }
        throw lastError;
    }

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the MailManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.MailManager,
            "MailManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.MailManager,
                "  Phase 1: Stopping active processes",
            );

            logger.info(LOGS.MailManager, "    Stopping mail sync");
            this.mailSync._cleanup();
        } catch (error) {
            logger.error(
                LOGS.MailManager,
                `MailManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(LOGS.MailManager, "  Phase 2: Clearing internal state");

            // Clear all mail records and caches
            logger.info(
                LOGS.MailManager,
                "    Clearing mail records and caches",
            );
            this.mailRecord.byInternetMessageId = {};

            // Reset processing flags and states
            logger.info(LOGS.MailManager, "    Resetting processing flags");
            this._firstUserAnalysisLaunched = false;

            // Clear folder IDs
            logger.info(LOGS.MailManager, "    Clearing folder references");
            this.mailRecord.inboxFolderId = undefined;
            this.mailRecord.deleteditemsFolderId = undefined;
            this.mailRecord.sentitemsFolderId = undefined;
            this.mailRecord.archiveFolderId = undefined;
            this.mailRecord.junkemailFolderId = undefined;

            this.lastNavigationUpdatedExecutionTime = 0;

            // Reset debounce value
            logger.info(LOGS.MailManager, "    Clearing debounce references");
            this.navigationNeedUpdateInterval = null;

            // Clear attachments-fetched guard
            this.mailRecord._attachmentsFetchedSet.clear();

            // Clear attachment previews Record
            this.mailRecord.attachmentPreviewsRecordByKey = {};

            this.mailRecord.mailViewContentRecordByInternetMessageId = {};

            this.mailReader._cleanup();
            this.mailViews._cleanup();

            logger.info(LOGS.MailManager, "    Resetting promise queues");
            this.processingPromise = Promise.resolve();
        } catch (error) {
            logger.error(
                LOGS.MailManager,
                `MailManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        logger.info(
            LOGS.MailManager,
            "MailManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    public _registerEvents() {
        this["inbox-folder-id-updated_handler"] =
            this["inbox-folder-id-updated_handler"].bind(this);
        this["mail-folder-id-updated_handler"] =
            this["mail-folder-id-updated_handler"].bind(this);

        this["mail-analysis-completed_handler"] =
            this["mail-analysis-completed_handler"].bind(this);
        this["mail-sent_handler"] = this["mail-sent_handler"].bind(this);
        this["mails-new-mails_handler"] =
            this["mails-new-mails_handler"].bind(this);

        this["user-todos-updated_handler"] =
            this["user-todos-updated_handler"].bind(this);
        this["max-analysed-mails-released_handler"] =
            this["max-analysed-mails-released_handler"].bind(this);

        this["navigation-need-update_handler"] =
            this["navigation-need-update_handler"].bind(this);
        this["navigation-asking-for-update_handler"] =
            this["navigation-asking-for-update_handler"].bind(this);

        this["custom-view-created_handler"] =
            this["custom-view-created_handler"].bind(this);
        this["custom-view-updated_handler"] =
            this["custom-view-updated_handler"].bind(this);
        this["custom-view-deleted_handler"] =
            this["custom-view-deleted_handler"].bind(this);

        this["co2-updated_handler"] = this["co2-updated_handler"].bind(this);

        this["verify-existing-mails-for-view_handler"] =
            this["verify-existing-mails-for-view_handler"].bind(this);

        this.eventBus.on(
            "inbox-folder-id-updated",
            this["inbox-folder-id-updated_handler"],
        );
        this.eventBus.on(
            "mail-folder-id-updated",
            this["mail-folder-id-updated_handler"],
        );

        this.eventBus.on(
            "mail-analysis-completed",
            this["mail-analysis-completed_handler"],
        );
        this.eventBus.on("mail-sent", this["mail-sent_handler"]);
        this.eventBus.on("mails-new-mails", this["mails-new-mails_handler"]);

        this.eventBus.on(
            "user-todos-updated",
            this["user-todos-updated_handler"],
        );
        this.eventBus.on(
            "max-analysed-mails-released",
            this["max-analysed-mails-released_handler"],
        );

        this.eventBus.on(
            "navigation-need-update",
            this["navigation-need-update_handler"],
        );
        this.eventBus.on(
            "navigation-asking-for-update",
            this["navigation-asking-for-update_handler"],
        );

        this.eventBus.on(
            "custom-view-created",
            this["custom-view-created_handler"],
        );
        this.eventBus.on(
            "custom-view-updated",
            this["custom-view-updated_handler"],
        );
        this.eventBus.on(
            "custom-view-deleted",
            this["custom-view-deleted_handler"],
        );

        this.eventBus.on("co2-updated", this["co2-updated_handler"]);

        this.eventBus.on(
            "verify-existing-mails-for-view",
            this["verify-existing-mails-for-view_handler"],
        );
    }

    private async "mail-analysis-completed_handler"(
        event: QualifiedMail,
    ): Promise<void> {
        await this._updateRecordWithAnalysis(event);
    }

    private async "inbox-folder-id-updated_handler"(
        inboxFolderId: string,
    ): Promise<void> {
        logger.info(
            LOGS.MailManager,
            `MailManager processing <inbox-folder-id-updated> : ${inboxFolderId}`,
        );
        if (!this.mailRecord.inboxFolderId && inboxFolderId) {
            this.mailRecord.inboxFolderId = inboxFolderId;
        }
    }

    private async "mail-folder-id-updated_handler"({
        folderName,
        folderId,
    }: {
        folderName: string;
        folderId: string;
    }): Promise<void> {
        logger.info(
            LOGS.MailManager,
            `MailManager processing <mail-folder-id-updated> event with folderName ${folderName} and folderID ${folderId}`,
        );

        switch (folderName) {
            case "deleteditems":
                if (!this.mailRecord.deleteditemsFolderId && folderId) {
                    this.mailRecord.deleteditemsFolderId = folderId;
                }
                break;

            case "sentitems":
                if (!this.mailRecord.sentitemsFolderId && folderId) {
                    this.mailRecord.sentitemsFolderId = folderId;
                }
                break;

            case "archive":
                if (!this.mailRecord.archiveFolderId && folderId) {
                    this.mailRecord.archiveFolderId = folderId;
                }
                break;

            case "junkemail":
                if (!this.mailRecord.junkemailFolderId && folderId) {
                    this.mailRecord.junkemailFolderId = folderId;
                }
                break;

            default:
                logger.warn(
                    LOGS.MailManager,
                    `Error with folder ${folderName} : ${folderId}`,
                );
                break;
        }
    }

    private async "mail-sent_handler"(mailId: string): Promise<void> {
        logger.info(
            [LOGS.MailManager],
            `MailManager processing <mail-sent> event for mailId ${mailId} ...`,
        );
        const sentMail = await this.dataManager.getMailById(mailId);

        // If mail not in record, add it
        if (
            sentMail != null &&
            !this.mailRecord.byInternetMessageId[sentMail.internetMessageId]
        ) {
            this.mailSync._setMailInRecord(sentMail);
            // Resolve inline CID references (e.g. signature images) before emitting to the frontend.
            // getMailById does not fetch attachments, so without this the HTML contains raw cid: URLs
            // that the browser cannot render (net::ERR_UNKNOWN_URL_SCHEME).
            await this.mailReader
                .getMailFullBodyContentWithAttachments(
                    sentMail.internetMessageId,
                )
                .catch(() => {});
        }

        this.eventBus.emit("thread-updated", sentMail);
    }

    private async "mails-new-mails_handler"(
        newFullMails: Mail[],
    ): Promise<void> {
        logger.info(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `MailManager processing <mails-new-mails> event with ${newFullMails.length} new mails ...`,
        );
        newFullMails.forEach((newMail) => {
            this.eventBus.emit("thread-updated", newMail);
        });
    }

    private async "user-todos-updated_handler"(
        userTodos: TodoItem[],
    ): Promise<void> {
        const updatedMailsInternetMessageIds = new Set<string>(); // Track which mails have been modified
        logger.info(
            [LOGS.MailManager],
            `MailManager processing <user-todos-updated> event with ${userTodos.length} todos ...`,
        );

        userTodos.forEach((updatedTodo) => {
            // Le todo est keyé internetMessageId → copie canonique inbox.
            const mail = this.mailSync._getCanonicalMail(
                updatedTodo.internetMessageId,
            );

            if (!mail) {
                return;
            }

            const mailToUpdate = mail.internetMessageId;

            // Copie hors inbox (corbeille, archive) : sans analyse, ses todos ne vivent que dans le widget
            if (!mail.aiDetails) {
                return;
            }

            if (!mail.aiDetails.toDo) {
                logger.warn(
                    LOGS.MailManager,
                    `No toDo array found in mail ${mailToUpdate}, creating it for todo ${updatedTodo.id}`,
                );
                mail.aiDetails.toDo = [];
            }

            // Check if this is a new todo that needs to be added
            const todoIndex = mail.aiDetails.toDo.findIndex(
                (todoItem) => todoItem.id === updatedTodo.id,
            );

            // Le Record est un cache : son `aiDetails` peut précéder la création du todo.
            // Absent ici ne veut donc PAS dire orphelin en base — ne rien en conclure.
            if (todoIndex === -1) {
                logger.warn(
                    LOGS.MailManager,
                    `Todo ${updatedTodo.id} absent du cache du mail ${mailToUpdate} — ignoré`,
                );
                return;
            }

            // At this point, we know the todo exists, so get it for comparison
            const currentTodo = mail.aiDetails.toDo[todoIndex];

            // More reliable comparison - check specific properties that might change
            const hasChanged =
                currentTodo.id !== updatedTodo.id ||
                currentTodo.done !== updatedTodo.done ||
                currentTodo.linkedEventId !== updatedTodo.linkedEventId ||
                currentTodo.taskSummary !== updatedTodo.taskSummary ||
                currentTodo.isUserAccepted !== true;

            if (hasChanged) {
                logger.info(
                    LOGS.MailManager,
                    `Updating todo ${updatedTodo.id} in mail ${mailToUpdate}`,
                );

                // Update the actual record (this modifies the original object reference)
                mail.aiDetails.toDo[todoIndex] = {
                    ...updatedTodo,
                    isUserAccepted: true,
                };

                // Mark this mail as updated
                updatedMailsInternetMessageIds.add(mailToUpdate);
            } else {
            }
        });

        // Emit mail-updated event only once per modified mail
        updatedMailsInternetMessageIds.forEach((mailInternetMessageId) => {
            const mail = this.mailSync._getCanonicalMail(mailInternetMessageId);
            if (mail) {
                this.eventBus.emit("mail-updated", mail);
            }
        });
    }

    // La relance est refusée tant que le flag est faux : le rafraîchir d'abord n'est pas optionnel.
    private async "max-analysed-mails-released_handler"(): Promise<void> {
        try {
            await this.mailSync._refreshQuotaState();
            this._analysePendingInboxMails();
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `Quota release handling failed, analysis stays gated: ${error}`,
            );
        }
    }

    private async "navigation-need-update_handler"(): Promise<void> {
        logger.info(
            [LOGS.MailManager],
            `MailManager processing <navigation-need-update> event`,
        );

        this.mailViews._recalculateCounters();
    }

    private async "navigation-asking-for-update_handler"(): Promise<void> {
        logger.info(
            [LOGS.MailManager],
            `MailManager processing <navigation-asking-for-update> event`,
        );

        const now = Date.now();
        const timeSinceLastExecution =
            now - this.lastNavigationUpdatedExecutionTime;

        // Cancel previous timer if it exists
        if (this.navigationNeedUpdateInterval) {
            clearTimeout(this.navigationNeedUpdateInterval);
        }

        // Execute immediately if no recent execution
        if (timeSinceLastExecution >= this.NAV_UPDATE_DEBOUNCE_DELAY) {
            this.eventBus.emit("navigation-need-update");
            this.lastNavigationUpdatedExecutionTime = now;
        } else {
            // Otherwise debounce
            this.navigationNeedUpdateInterval = setTimeout(() => {
                this.eventBus.emit("navigation-need-update");
                this.lastNavigationUpdatedExecutionTime = Date.now();
                this.navigationNeedUpdateInterval = null;
            }, this.NAV_UPDATE_DEBOUNCE_DELAY);
        }
    }

    private async "custom-view-created_handler"(
        view: CustomView,
    ): Promise<void> {
        logger.info(
            LOGS.MailManager,
            `MailManager processing <custom-view-created> event`,
        );

        await this.mailViews._refreshCustomViewNavItems();
    }

    private async "custom-view-updated_handler"(
        view: CustomView,
    ): Promise<void> {
        logger.info(
            LOGS.MailManager,
            `MailManager processing <custom-view-updated> event`,
        );

        if (await this.mailViews._refreshCustomViewNavItems()) {
            this.eventBus.emit("navigation-need-update");
        }
    }

    private async "custom-view-deleted_handler"(viewId: string): Promise<void> {
        logger.info(
            LOGS.MailManager,
            `MailManager processing <custom-view-deleted> event (${viewId})`,
        );

        if (await this.mailViews._refreshCustomViewNavItems()) {
            this.eventBus.emit("navigation-need-update");
        }
    }

    private async "co2-updated_handler"(CO2Saved: CO2Saved): Promise<void> {
        logger.info(
            [LOGS.MailManager, LOGS.MailManager],
            `MailManager processing <co2-updated> event`,
        );
        logger.info(
            [LOGS.MailManager, LOGS.MailManager],
            `CO2Saved : ${CO2Saved}`,
        );
    }

    private async "verify-existing-mails-for-view_handler"(payload: {
        internetMessageIds: string[];
    }): Promise<void> {
        void this._verifyExistingMailsForView(payload.internetMessageIds);
    }

    // #endregion Event Handlers

    // #region Public Methods

    // #region Analysis quota accessors

    public getCurrentMonthEmailCount(): number | null {
        return this.analysisState.currentMonthEmailCount;
    }

    // Le watermark du polling est passé devant ces mails : rien d'autre ne les reproposera.
    private _analysePendingInboxMails(): void {
        // Sans les ids de dossiers le filtre inbox écarterait tout le Record, événement consommé pour rien.
        if (!this.mailRecord.inboxFolderId) {
            logger.warn(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                "Quota released but inboxFolderId not resolved yet - re-analysis skipped",
            );
            return;
        }

        const pending = new Map<string, BaseMail>();

        for (const copy of this.mailSync._allCopies()) {
            if (isQualifiedMail(copy)) continue;
            // En aval, le découpage au quota travaille sur une liste triée par date : des copies Sent anciennes mangeraient la fenêtre.
            if (!this.mailSync._isInboxFolder(copy.parentFolderId)) continue;
            if (pending.has(copy.internetMessageId)) continue;

            pending.set(copy.internetMessageId, copy as BaseMail);
        }

        if (pending.size === 0) {
            logger.info(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                "No pending mail to re-analyse after quota release",
            );
            return;
        }

        const batch = [...pending.values()].slice(0, PENDING_REANALYSIS_CAP);

        logger.warn(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `Quota released - re-analysing ${batch.length} of ${pending.size} pending inbox mail(s)`,
        );

        void this.mailSync._processEmailsWithAI(batch);
    }

    public getMonthlyAnalysedMailsLimit(): number | null {
        return this.analysisState.monthlyAnalysedMailsLimit;
    }

    // #endregion Analysis quota accessors

    // #region Email lifecycle

    /**
     * pour les favorite Views await fetchMissingMailsIfAnyByInternetMessageIds -
     * emit('custom-view-updated') -
     *
     *
     */
    public async fetchFocusStartupMails(
        focusMailsInternetMessageIds: string[],
    ): Promise<void> {
        logger.info([LOGS.MailManager], `fetchFocusStartupMails()`);

        const fetchStartTime = performance.now();
        logger.perf([LOGS.MailManager], `Exécution start`, fetchStartTime);

        // Fetch Focus mails
        logger.info(
            [LOGS.MailManager],
            `  fetch missing startup Focus mails ...`,
        );

        // Fetch missing mails in Record
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            focusMailsInternetMessageIds,
            `MailManager.fetchFocusStartupMails)`,
        );
        logger.perf(
            [LOGS.MailManager],
            `Exécution fetch Focus    `,
            fetchStartTime,
        );
        logger.perf(
            [LOGS.MailManager],
            `Exécution totale         `,
            fetchStartTime,
        );

        logger.info(
            LOGS.TESTS,
            "====================================== STARTUP fetchMissingMailsIfAnyByInternetMessageIds IS READY ===========================================",
        );

        // Reorder mails with fake ranking (saved order in DB)
        let index = 100;

        logger.warn(LOGS.MailManager, "fake Focus ranking for startup process");
        focusMailsInternetMessageIds.forEach((focusMailId) => {
            const mail = this.mailSync._getCanonicalMail(focusMailId);
            if (mail) {
                mail._ranking = {
                    totalScore: Math.max(index--, 0),

                    /** Détail des scores par dimension de calcul */
                    detailedScores: {
                        /** Score basé sur les correspondances de personnes (après pondération) */
                        personnesScore: 0,
                        /** Score basé sur les correspondances de titres/contenus (après pondération) */
                        titresScore: 0,
                        /** Score basé sur les correspondances d'équipes/projets (après pondération) */
                        teamsScore: 0,
                        /** Score basé sur la présence de mon mail dans dest ou cc */
                        addresseeScore: 0,
                        /** Score basé sur l'analyse de ton (après pondération) */
                        toneScore: 0,
                    },
                    completeDetailedScores: {},
                };
            } else {
                // [#1672] Cas NORMAL et attendu : le mail Focus du snapshot DB a été supprimé en externe
                // (Outlook) puis évincé du Record/store par `fetchMissingMailsIfAnyByInternetMessageIds`
                // (notFound). Il n'y a donc rien à ranker — ce n'est pas une erreur. On trace en `logger.warn`
                // (au lieu de `logger.error`) pour ne pas polluer les erreurs de démarrage lors d'une
                // réconciliation de suppression externe. Le compteur Focus est déjà corrigé par l'éviction.
                logger.warn(
                    [LOGS.MailManager],
                    `fetchFocusStartupMails - Focus mail <${focusMailId}> absent du Record après fetch (probablement supprimé en externe → évincé) : ranking ignoré`,
                );
            }
        });

        logger.info(
            LOGS.TESTS,
            `--- fetchFocusStartupMails fake ranking DONE ---`,
        );
        logger.info(
            LOGS.TESTS,
            "this.mailRecord.byInternetMessageId",
            this.mailRecord.byInternetMessageId,
        );

        logger.info(
            LOGS.TESTS,
            "====================================== STARTUP FOCUS MAILS FAKE RANKING IS READY ===========================================",
        );

        // Émet l'event de mise à jour de navigation pour le SmartLabel Focus.
        // Nouvelle archi : Focus n'est plus une CustomView DB — on émet custom-view-list-updated
        // avec l'id SmartLabel.Focus (valeur utilisée par le Front comme identifiant de vue Focus).
        logger.info(
            LOGS.TESTS,
            `MailManager.fetchFocusStartupMails emit 'custom-view-list-updated' for Focus`,
        );
        this.eventBus.emit("custom-view-list-updated", {
            customViewId: SmartLabel.Focus,
        });

        logger.perf(
            [LOGS.TESTS],
            `=== Exécution totale fetchFocusStartupMails === `,
            fetchStartTime,
        );
    }

    /**
     * For favorite views - await fetchMissingMailsIfAnyByInternetMessageIds -
     * emit('custom-view-updated') -
     *
     * For General Mail - await fetchMissingMailsIfAnyByInternetMessageIds -
     * emit('custom-view-updated') -
     *
     * For other views  - await fetchMissingMailsIfAnyByInternetMessageIds -
     * emit('custom-view-updated') -
     *
     */
    public async fetchOtherStartupMails(
        favoriteCustomViewsMailsInternetMessageIds: string[],
        otherViewsMailsInternetMessageIds: string[],
    ): Promise<void> {
        logger.info([LOGS.MailManager], `fetchOtherStartupMails()`);

        const fetchStartTime = performance.now();
        logger.perf([LOGS.MailManager], `Exécution start`, fetchStartTime);

        // TEST 16 first, all view at once
        // const allIds = [...new Set([...focusMailsInternetMessageIds, ...favoriteCustomViewsMailsInternetMessageIds, ...otherViewsMailsInternetMessageIds])];
        // await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(allIds);
        // ENDTEST

        // Fetch favorite CustomViews mails
        logger.info(
            [LOGS.MailManager],
            `  fetch missing startup favorite CustomViews mails ...`,
        );

        // Fetch missing mails in Record
        const favoriteStartTime = performance.now();
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            favoriteCustomViewsMailsInternetMessageIds,
            `MailManager.fetchOtherStartupMails.customViews`,
        );
        logger.perf(
            [LOGS.MailManager],
            `Exécution fetch Favorite`,
            favoriteStartTime,
        );
        logger.perf(
            [LOGS.MailManager],
            `Exécution totale        `,
            fetchStartTime,
        );

        // Emit event to update nav
        const favoriteViews = await this.customViewManager._getFavoriteViews();
        if (favoriteViews && favoriteViews.length > 0) {
            favoriteViews.forEach((view) => {
                logger.info(
                    LOGS.TESTS,
                    `CustomViewManager.fetchStartupMails emit 'custom-view-updated' for favorite view <${view.name}>`,
                );
                this.eventBus.emit(
                    "custom-view-updated",
                    JSON.parse(JSON.stringify(view)),
                );
            });
        } else {
        }

        // Fetch General mails
        logger.info(
            [LOGS.MailManager],
            `  fetch missing startup General mails ...`,
        );

        // Fetch missing mails in Record
        // WS2 : fetchGeneralInternetMessageIds renvoie désormais des MailCopyRef[] ; pont minimal vers le consommateur string[] (slot-aware = WS3/WS4).
        const generalInboxMailsInternetMessageIds = (
            (await this.dataManager.fetchGeneralInternetMessageIds(
                MailFolder.Inbox,
                35,
            )) ?? []
        ).map((ref) => ref.internetMessageId);
        const generalStartTime = performance.now();
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            generalInboxMailsInternetMessageIds,
            `MailViewManager.fetchOtherStartupMails.General`,
        );
        logger.perf(
            [LOGS.MailManager],
            `Exécution fetch General    `,
            generalStartTime,
        );
        logger.perf(
            [LOGS.MailManager],
            `Exécution totale           `,
            fetchStartTime,
        );

        // Fetching other CustomViews mails
        logger.info(
            [LOGS.MailManager],
            `  fetch missing startup other CustomViews mails ...`,
        );

        // Fetch missing mails in Record
        const otherViewsStartTime = performance.now();
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            otherViewsMailsInternetMessageIds,
            `MailViewManager.fetchOtherStartupMails.Other`,
        );
        logger.perf(
            [LOGS.MailManager],
            `Exécution fetch General    `,
            otherViewsStartTime,
        );

        // Emit event to update nav
        const otherViews = await this.customViewManager.getCustomViews();
        if (otherViews && otherViews.length > 0) {
            otherViews.forEach((view) => {
                logger.info(
                    LOGS.TESTS,
                    `CustomViewManager.fetchOtherStartupMails emit 'custom-view-updated' for Other view <${view.name}>`,
                );
                this.eventBus.emit(
                    "custom-view-updated",
                    JSON.parse(JSON.stringify(view)),
                );
            });
        } else {
        }

        logger.perf(
            [LOGS.MailManager],
            `=== Exécution totale fetchOtherStartupMails === `,
            fetchStartTime,
        );
    }

    /**
     * @method firstUserEmailsAnalysis
     * Calcule la première liste des emails d'un nouvel utilisateur -
     * (not awaited) fetchMissingMailsIfAnyByInternetMessageIds -
     * await 4 secondes pour le logo avant Dashboard
     *
     * ═══════════════════════════════════════════════════════════════════════════
     * @output {Promise<void>}
     * @throws {Error} Si les gestionnaires requis ne sont pas initialisés
     */
    public async firstUserEmailsAnalysis(): Promise<void> {
        const startTime = performance.now();
        logger.info(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `MailManager.firstUserEmailsAnalysis`,
        );

        try {
            // Obtenir les derniers e-mails de l'utilisateur

            // AB#1621 — Limites pilotées par la LICENCE (`initialAnalysis.byMail/byDay`), plus de 300/15 hardcodés.
            const { byMail, byDay } = this.mailSync._getInitialAnalysisLimits();

            // Ancre de la fenêtre = registrationDate − byDay (alignement EXACT avec le plancher provisoire backend).
            // Repli `now − byDay` si registrationDate absente (ne devrait pas arriver pour un nouvel utilisateur).
            const registrationDateValue =
                this.profileLoader.cachedProfile?.profile?.registrationDate;
            const anchorDate = registrationDateValue
                ? new Date(registrationDateValue)
                : new Date();
            const windowStart = new Date(
                anchorDate.getTime() - byDay * 24 * 60 * 60 * 1000,
            ).toISOString();
            logger.info(
                [LOGS.AnalysisLifecycle],
                `firstUserEmailsAnalysis — fenêtre rétrospective byMail=${byMail} byDay=${byDay} (anchor=${anchorDate.toISOString()}, windowStart=${windowStart})`,
            );

            // Budget = byMail mails les plus récents depuis windowStart (l'API plafonne la pagination Graph).
            let lastMailsIdsLimitedByDate: MailIds[] = [];
            try {
                lastMailsIdsLimitedByDate =
                    await this.apiManager.GetMailIdsSince(windowStart, byMail);
            } catch (error) {
                logger.error(
                    [LOGS.MailManager],
                    `Error in firstUserEmailsAnalysis : ${error}`,
                );
                logger.warn(
                    LOGS.MailManager,
                    "Retrying once after 2s delay ...",
                );

                await new Promise((resolve) => setTimeout(resolve, 2000));
                lastMailsIdsLimitedByDate =
                    await this.apiManager.GetMailIdsSince(windowStart, byMail);
            }

            const lastInternetMessageIdsLimitedByDate =
                lastMailsIdsLimitedByDate.map((m) => m.internetMessageId);
            // Double sécurité : on ne garde que les byMail premiers (DESC ⇒ les plus récents).
            const budgetInternetMessageIds =
                lastInternetMessageIdsLimitedByDate.slice(0, byMail);
            logger.info(
                [LOGS.AnalysisLifecycle],
                `GetMailIdsSince → ${lastMailsIdsLimitedByDate.length} ids inbox dans la fenêtre ${byDay}j ; budget retenu=${budgetInternetMessageIds.length}/${byMail} ⇒ ${budgetInternetMessageIds.length >= byMail ? "byMail BORNE (plancher attendu = mail-frontière)" : "byDay borne (plancher provisoire attendu)"}`,
            );

            // Guard contre double lancement (ex: retry depuis _initialize() après un échec de GetMailIdsSince)
            if (this._firstUserAnalysisLaunched) {
                logger.warn(
                    [LOGS.MailManager, LOGS.AnalysisLifecycle],
                    "firstUserEmailsAnalysis: analysis already launched, skipping duplicate",
                );
                return;
            }
            this._firstUserAnalysisLaunched = true;

            // Collecteur de Promises : chaque appel à _processEmailsWithAI y pousse sa Promise.
            // Alimenté indirectement via la chaîne :
            //   fetchMissingMailsIfAnyByInternetMessageIds → _fetchAndProcessMails → _addMailsToRecord
            // Une fois le fetch terminé, Promise.allSettled(analysisPromises) permet d'attendre
            // la fin de TOUTES les analyses IA (résout même si certains batches échouent individuellement).
            const analysisPromises: Promise<void>[] = [];

            // Fire-and-forget : on chaîne .then/.catch pour émettre initial-analysis-completed
            // après la fin du FETCH et de l'ANALYSE COMPLÈTE de tous les mails initiaux.
            this.mailSync
                .fetchMissingMailsIfAnyByInternetMessageIds(
                    budgetInternetMessageIds,
                    `MailManager.firstUserEmailsAnalysis`,
                    false,
                    250,
                    1,
                    analysisPromises,
                )
                .then(async () => {
                    // ── AB#1621 — Bascule dashboard 30s (⚠️ À SUPPRIMER une fois la durée réelle confirmée) ──
                    // ANCIEN comportement : on débloquait au min(analyse, 25s) via Promise.race, sous le timeout
                    // de 30s de ServicesProvider. On l'a COMMENTÉ (pas supprimé) pour attendre la VRAIE fin de
                    // l'analyse et mesurer sa durée réelle (décision Lot 2 batch/reprise sur 300 mails).
                    // await Promise.race([
                    //   Promise.allSettled(analysisPromises),
                    //   new Promise<void>(resolve => setTimeout(resolve, 25000))
                    // ]);
                    // NOUVEAU : warn d'observabilité à 30s (n'interrompt PLUS, informe seulement) + attente réelle.
                    const thirtySecWarn = setTimeout(
                        () =>
                            logger.warn(
                                [LOGS.AnalysisLifecycle],
                                `Initial analysis: 30s dépassé, toujours en cours`,
                            ),
                        30000,
                    );
                    await Promise.allSettled(analysisPromises);
                    clearTimeout(thirtySecWarn);
                    const elapsedSec = (
                        (performance.now() - startTime) /
                        1000
                    ).toFixed(1);
                    logger.info(
                        [LOGS.AnalysisLifecycle, LOGS.AB1725],
                        `[STARTUP-TIMING] Initial analysis took ${elapsedSec} seconds`,
                    );
                    // ────────────────────────────────────────────────────────────────────────────────────────

                    // AB#1621 — Calcul + persistance (avec retries) du plancher d'analyse (mail-frontière du budget).
                    // Échec de persistance = NON bloquant (INVESTIGATION §5.6 cas 6) : le plancher provisoire
                    // `registration − byDay` (appliqué par le garde-fou backend) protège déjà la fenêtre rétrospective.
                    // On logue l'anomalie mais on poursuit le démarrage normalement (pas de 'startup-failed', qui
                    // s'afficherait par-dessus un dashboard déjà ouvert si l'analyse dépasse le fallback 30 s).
                    const floorPersisted =
                        await this._computeAndPersistAnalysisFloor(
                            budgetInternetMessageIds,
                            anchorDate,
                            byMail,
                            byDay,
                        );
                    if (!floorPersisted) {
                        logger.error(
                            [LOGS.AnalysisLifecycle],
                            "Plancher d'analyse NON persisté après retries — poursuite sur plancher provisoire (non bloquant)",
                        );
                    }

                    logger.info(
                        [LOGS.MailManager, LOGS.AnalysisLifecycle],
                        "firstUserEmailsAnalysis: full analysis done — emitting initial-analysis-completed",
                    );
                    this.eventBus.emit("initial-analysis-completed");
                })
                .catch((error) => {
                    logger.error(
                        [LOGS.MailManager, LOGS.AnalysisLifecycle],
                        `firstUserEmailsAnalysis: fetchMissingMailsIfAnyByInternetMessageIds failed: ${error}`,
                    );
                    // Émettre quand même pour ne pas bloquer ServicesProvider jusqu'au timeout 30s
                    this.eventBus.emit("initial-analysis-completed");
                });

            logger.info(
                [LOGS.MailManager],
                `  firstUserEmailsAnalysis done - fetchMissingMailsIfAnyByInternetMessageIds launched not awaited.`,
            );
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `Error in MailManager.analyseUserEmails: ${error}`,
            );
            throw error;
        }
    }

    /**
     * AB#1621 — Calcule le plancher d'analyse (borne basse de la fenêtre budgétaire) et le persiste
     * **set-once** via `PATCH /me/userProfile`. L'API reste l'autorité (validation + set-once + garde-fou) ;
     * le SDK ne fait que proposer la donnée qu'il a déjà en main.
     *
     * Robustesse « anti-date-pourrie » (INVESTIGATION §5.2/§5.6) — on ne resserre le plancher que sur
     * une donnée POSITIVE, toute incertitude dégénère vers `anchor − byDay` (jamais trop récent) :
     *  - `< byMail` mails dans la fenêtre  → byDay a borné  → floor = anchor − byDay (provisoire).
     *  - `byMail` atteint                  → byMail a borné → floor = sentDateTime(mail-frontière) ?? (anchor − byDay).
     *  - mail-frontière absent / sans date → repli provisoire.
     *
     * @returns `true` si le plancher a été persisté (PATCH OK, éventuellement après retries) ; `false` si
     *          la persistance a échoué durablement (non bloquant : l'appelant logue et poursuit sur le
     *          plancher provisoire `registration − byDay`).
     */
    private async _computeAndPersistAnalysisFloor(
        budgetInternetMessageIds: string[],
        anchorDate: Date,
        byMail: number,
        byDay: number,
    ): Promise<boolean> {
        logger.info(
            [LOGS.AnalysisLifecycle],
            `_computeAndPersistAnalysisFloor: ENTRÉE budget=${budgetInternetMessageIds.length}/${byMail} byDay=${byDay} anchor=${anchorDate.toISOString()} byMailBorné=${budgetInternetMessageIds.length >= byMail}`,
        );
        // I/O : lire la date du mail-frontière (dernier id en DESC = le plus ancien) depuis le Record.
        let frontierSentDate: Date | null = null;
        if (budgetInternetMessageIds.length >= byMail) {
            const frontierId =
                budgetInternetMessageIds[budgetInternetMessageIds.length - 1];
            const frontierMail =
                this.mailSync._getMailFromRecordById(frontierId);
            frontierSentDate = frontierMail?.sentDateTime
                ? new Date(frontierMail.sentDateTime)
                : null;
            logger.info(
                [LOGS.AnalysisLifecycle],
                `mail-frontière: id=${frontierId} trouvéDansRecord=${!!frontierMail} sentDateTime=${frontierSentDate ? frontierSentDate.toISOString() : "null"}`,
            );
            if (!frontierSentDate || isNaN(frontierSentDate.getTime())) {
                logger.warn(
                    [LOGS.AnalysisLifecycle],
                    `mail-frontière (${frontierId}) introuvable/sans date dans le Record → repli plancher provisoire`,
                );
            }
        }

        // Calcul PUR (déterministe, testé en isolation — cf. computeAnalysisFloor.test.ts).
        const { floor, source } = computeAnalysisFloor({
            budgetCount: budgetInternetMessageIds.length,
            byMail,
            byDay,
            anchorDate,
            frontierSentDate,
        });
        const floorIso = floor.toISOString();

        logger.info(
            [LOGS.AnalysisLifecycle],
            `plancher calculé floor=${floorIso} (budget=${budgetInternetMessageIds.length}/${byMail}, byDay=${byDay}, source=${source})`,
        );

        // Persistance set-once avec retries : la pose du plancher est l'unique matérialisation du cap byMail.
        const PATCH_MAX_ATTEMPTS = 3;
        for (let attempt = 1; attempt <= PATCH_MAX_ATTEMPTS; attempt++) {
            let persisted = false;
            try {
                persisted = await this.apiManager.updateUserProfile({
                    analysisFloorDate: floorIso,
                });
            } catch (error) {
                logger.warn(
                    [LOGS.AnalysisLifecycle],
                    `PATCH plancher tentative ${attempt}/${PATCH_MAX_ATTEMPTS} a levé une exception: ${error}`,
                );
            }

            if (persisted) {
                // Refléter localement pour le pré-filtre de la session courante (sans refetch du profil).
                this.profileLoader.updateCachedProfile((userProfile) => ({
                    ...userProfile,
                    profile: {
                        ...userProfile.profile,
                        analysisFloorDate: floorIso,
                    },
                }));
                logger.info(
                    [LOGS.AnalysisLifecycle],
                    `plancher persisté (set-once) avec succès (tentative ${attempt}/${PATCH_MAX_ATTEMPTS}, floor=${floorIso})`,
                );
                return true;
            }

            logger.warn(
                [LOGS.AnalysisLifecycle],
                `PATCH plancher tentative ${attempt}/${PATCH_MAX_ATTEMPTS} échouée`,
            );
            if (attempt < PATCH_MAX_ATTEMPTS) {
                await new Promise((resolve) =>
                    setTimeout(resolve, 1000 * attempt),
                ); // backoff linéaire 1s, 2s
            }
        }

        logger.error(
            [LOGS.AnalysisLifecycle],
            `persistance du plancher ÉCHOUÉE après ${PATCH_MAX_ATTEMPTS} tentatives (floor=${floorIso}) — le cap byMail ne serait pas matérialisé`,
        );
        return false;
    }

    // #region Attachment previews (Record dédié — modèle FetchMissingIfAny)

    // #endregion Attachment previews

    //#endregion Email lifecycle

    // #region Dashboard

    // #endregion Dashboard

    // #region Navigation

    // #region Navigation private methods

    // #endregion Navigation private methods

    // #endregion Navigation

    // #region Mail details

    // #endregion Mail details

    // #region Mail actions

    // #endregion Mail actions

    // #region Utils

    // #endregion Utils

    // #region bulkActions

    // #endregion bulkActions

    //#region Mail with Agenda Items
    //#endregion

    // #region Todos

    // #endregion Todos

    // #endregion Public Methods

    //====================================================================

    // #region Private Methods

    /**
     * @method _verifyExistingMailsForView
     * Vérifie auprès du provider l'état réel des mails déjà présents dans le Record
     * pour une vue donnée. Évince ceux qui se trouvent dans `deletedItems` ou qui
     * sont introuvables côté provider.
     *
     * Utilisé en background lors de la navigation vers un SmartLabel pour pallier
     * la désynchronisation backend (un mail supprimé côté Graph que le backend
     * ignore encore reste dans le Record sans ce mécanisme).
     *
     * @param internetMessageIds - IDs candidats à la vérification (typiquement la vue courante)
     */
    private async _verifyExistingMailsForView(
        internetMessageIds: string[],
    ): Promise<void> {
        const startTime = performance.now();

        // 1. Garde-fou
        //    On délègue la détection de suppression au backend (getMailsByInternetMessageIds),
        //    qui applique une vérification robuste avec mail témoin (retryFetchRepoOnlyMails) :
        //    pas de faux positif sur réponse provider partielle, et purge DB automatique des
        //    mails confirmés absents (deletePersistentMails). On abandonne donc l'ancien
        //    getMailsParentFolderIds (naïf, sans témoin → faux positifs → boucle évict↔re-add).

        // déc. #7 : getMailsByInternetMessageIds exige inboxFolderId (sinon 400 + purge à l'aveugle côté API).
        //           Sans contexte prêt → on saute la vérification (best-effort).
        if (!this.mailRecord.inboxFolderId) {
            logger.warn(
                [LOGS.MailManager],
                `_verifyExistingMailsForView - inboxFolderId not set yet, skipping`,
            );
            return;
        }

        // 2. Filtrer pour ne garder que les IDs présents dans le Record
        //    (AB#1645) NE PAS étendre aux ids hors-Record ici : cela ferait un 2e appel backend
        //    NON dédupé, concurrent du fetch contenu (`_fetchMissingCopies` → `_inFlightFetches`),
        //    → race sur `deletePersistentMails` côté backend (400 GETTING_BASE_MAIL_FAILED).
        //    Le retrait du store pour les mails supprimés hors-Record est piloté par le fetch contenu
        //    (chemin dédupé), cf. fetchMissingMailsIfAnyByInternetMessageIds (branche notFound + hors-Record).
        const idsInRecord = internetMessageIds.filter(
            (id) => id in this.mailRecord.byInternetMessageId,
        );

        if (idsInRecord.length === 0) {
            logger.info(
                [LOGS.MailManager],
                `_verifyExistingMailsForView - no mails in Record, skipping`,
            );
            return;
        }

        logger.info(
            [LOGS.MailManager],
            `_verifyExistingMailsForView - verifying ${idsInRecord.length} mails via backend (robuste)`,
        );

        // 3. Détection backend robuste (Graph + DB + retry témoin)
        let response: GetMailsFromInternetMessageIdsResponseBody;
        try {
            response = await this.apiManager.getMailsByInternetMessageIds(
                idsInRecord,
                this.mailRecord.inboxFolderId,
            );
        } catch (error) {
            logger.warn(
                [LOGS.MailManager],
                `_verifyExistingMailsForView - getMailsByInternetMessageIds failed, skipping eviction: ${error}`,
            );
            return;
        }

        // 4. Identifier les mails à évincer
        //    - notFound : confirmé absent côté provider+DB (déjà purgé en DB côté backend)
        //    - trash    : présent mais déplacé en corbeille → retirer de la vue + purger en DB
        const notFoundIds = response.notFoundMailsInternetMessageIds ?? [];

        const combinedMails = [
            ...response.qualifiedMails,
            ...response.baseMails,
        ];
        const trashIds = this.mailRecord.deleteditemsFolderId
            ? combinedMails
                  .filter(
                      (mail) =>
                          mail.parentFolderId ===
                          this.mailRecord.deleteditemsFolderId,
                  )
                  .map((mail) => mail.internetMessageId)
            : [];

        const idsToEvict = [...new Set([...notFoundIds, ...trashIds])];

        // Observabilité (debug suppression) : verdict détaillé par id renvoyé par le backend.
        // Permet de voir, pour chaque mail vérifié, quelle COPIE physique le backend a retournée
        // (id Graph + parentFolderId) et le verdict (found / trash / notFound). Crucial pour les
        // mails self-sent (1 internetMessageId = 2 copies Inbox+Sent) dont le comportement de
        // suppression dépend de la copie effectivement renvoyée.
        const verifyVerdicts = idsInRecord.map((internetMessageId) => {
            const returned = combinedMails.find(
                (m) => m.internetMessageId === internetMessageId,
            );
            return {
                internetMessageId,
                verdict: notFoundIds.includes(internetMessageId)
                    ? "notFound"
                    : returned &&
                        returned.parentFolderId ===
                            this.mailRecord.deleteditemsFolderId
                      ? "trash"
                      : returned
                        ? "kept"
                        : "absent-from-response",
                graphId: returned?.id ?? "(none)",
                parentFolderId: returned?.parentFolderId ?? "(none)",
            };
        });
        logger.info(
            [LOGS.MailManager],
            `_verifyExistingMailsForView - verdicts par id (deletedItemsFolderId=${this.mailRecord.deleteditemsFolderId})`,
            verifyVerdicts,
        );

        if (idsToEvict.length === 0) {
            logger.perf(
                LOGS.MailManager,
                `_verifyExistingMailsForView - no eviction needed (${idsInRecord.length} verified)`,
                startTime,
            );
            return;
        }

        // 5. Purge DB des mails en corbeille (les notFound sont déjà purgés côté backend)
        if (trashIds.length > 0) {
            // [WS-C / AB#1576] migré Core → Api-Backend
            await this.apiManager.deleteAnalyzedMailsByInternetMessageIds(
                trashIds,
            );
        }

        // 6. Éviction : retirer du Record et notifier
        logger.info(
            [LOGS.MailManager],
            `_verifyExistingMailsForView - evicting ${idsToEvict.length} mail(s) (notFound=${notFoundIds.length}, trash=${trashIds.length})`,
        );
        idsToEvict.forEach((internetMessageId) => {
            // AB#1900 — le verdict porte sur la copie inbox que CETTE vue représente, pas sur le message.
            // ⚠️ Cible TOUJOURS le slot inbox : le `parentFolderId` renvoyé par le backend est celui de la
            // copie TROUVÉE (la copie Corbeille sur un verdict `trash`), s'en servir la détruirait.
            // cf. docs/bugs/ab1900-eviction-copie-vs-message.md
            const inboxFolderId = this.mailRecord.inboxFolderId;
            const inboxCopy = inboxFolderId
                ? this.mailSync._getCopy(internetMessageId, inboxFolderId)
                : undefined;

            if (inboxCopy && inboxFolderId) {
                // WS5c-2 : uid capturé AVANT le retrait, émission APRÈS.
                const inboxUid = uniqueMailvistaId(inboxCopy);
                this.mailSync._removeCopyFromRecord(
                    internetMessageId,
                    inboxFolderId,
                );
                this.eventBus.emit("mail-deleted", {
                    uniqueMailvistaId: inboxUid,
                });
            }

            // Le message quitte la vue dans tous les cas — raison d'être de cette vérification.
            this.mailSync.smartLabelStore.removeMessage(internetMessageId);
        });
        this.eventBus.emit("navigation-asking-for-update");

        logger.perf(
            LOGS.MailManager,
            `_verifyExistingMailsForView - ${idsToEvict.length}/${idsInRecord.length} mails evicted`,
            startTime,
        );
    }

    /**
     * @method _updateRecordWithAnalysis
     * Met à jour un email du Record avec les résultats d'analyse IA -
     * Ajoute au besoin la catégorie #Important en DB -
     * Met à jour les customView
     * Emet un event d'update
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {any} emailAnalysis - Résultats de l'analyse IA
     * @output {void}
     * @private
     */
    private async _updateRecordWithAnalysis(
        emailAnalyzed: QualifiedMail,
    ): Promise<void> {
        let internetMessageId = emailAnalyzed?.internetMessageId;
        if (!internetMessageId) {
            logger.error(
                [LOGS.MailManager],
                `updateMailsWithAnalysis - internetMessageId not found in received emailAnalysis !!!`,
            );
            return;
        }
        const mail = this.mailSync._getCanonicalMail(internetMessageId);
        if (!mail) {
            logger.error(
                [LOGS.MailManager],
                `Mail ${internetMessageId} not found in current mails => no Ia Analysis update have been done !`,
            );
            return;
        }

        // Update Todos
        const entities =
            emailAnalyzed.aiDetails.entities?.map(
                (entity: any): MailEntity => ({
                    type: entity.type,
                    content: entity.content,
                }),
            ) || [];
        // Le payload ne porte pas l'imid par todo : il est estampé depuis le mail parent.
        const updatedTodos =
            emailAnalyzed.aiDetails.toDo?.map((todo) => {
                return {
                    ...todo,
                    internetMessageId,
                    checkedDate: todo.checkedDate ?? null,
                    // L'API fait foi : le cache du dashboard ignore les todos terminés et peut être périmé
                    isUserAccepted: todo.isUserAccepted ?? false,
                };
            }) || [];

        const aiDetails: MailAIDetails = {
            mailSummary: emailAnalyzed.aiDetails.mailSummary || "",
            longMailSummary: emailAnalyzed.aiDetails.longMailSummary || "",
            categories: emailAnalyzed.aiDetails.categories || [],
            entities: entities,
            analyzedMailDate: emailAnalyzed.aiDetails.analyzedMailDate,
            keywords: emailAnalyzed.aiDetails.keywords || [],
            toDo: updatedTodos,
            toneAnalysis: emailAnalyzed.aiDetails.toneAnalysis,
        };

        // Une analyse n'annule jamais une intention utilisateur explicite — règle que le backend
        // applique déjà sur l'auto-unfocus des réunions passées.
        const hasExplicitUserIntent =
            mail.isUserEdited === true || mail.isUserProcessed === true;

        if (hasExplicitUserIntent) {
            logger.warn(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `_updateRecordWithAnalysis - intention utilisateur conservée pour ${internetMessageId} : isFocus/isUserProcessed/smartLabel non réécrits par l'analyse`,
            );
        } else {
            mail.isFocus = emailAnalyzed.isFocus;
            mail.isUserProcessed = emailAnalyzed.isUserProcessed;
            mail.isUserEdited = emailAnalyzed.isUserEdited ?? false;
        }

        // Score is calculated by the backend in analysis-save-mail
        if (emailAnalyzed.score != null) {
            mail.score = emailAnalyzed.score;
        }

        // Save mail in Record
        mail.aiDetails = aiDetails;
        if (!hasExplicitUserIntent) {
            mail.smartLabel = emailAnalyzed.smartLabel;
        }
        // sentDateTimeMailvista — clé tri Focus groupe 1 (calculée backend depuis l'expéditeur)
        mail.sentDateTimeMailvista = emailAnalyzed.sentDateTimeMailvista;

        // Size Update
        mail.componentSize = this.mailSync._determineMailComponentSize(mail);

        // Send event to notify that a mail has been updated
        // CustomViewManager handles both SmartLabel store (_updateSmartLabelEntry)
        // and DB CustomViews (_updateAllCustomViewsIfNeededAndSendEvent) on this event.
        logger.info(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            "updateMailWithAnalysis - Emitting mail-updated event",
        );
        this.eventBus.emit("mail-updated", mail);

        return;
    }

    // #endregion Utils

    // #endregion Private Methods
}
